/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 16.5 — Employee Asset Handover
 * HR/IT issues onboarding assets → Super Admin / RM approves → Employee
 * e-signs in portal → assets in use → return flow on separation/transfer.
 */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Briefcase,
  Plus,
  RefreshCw,
  Laptop,
  Phone,
  KeySquare,
  Boxes,
  Printer,
  PackageOpen,
  CheckCircle2,
  X,
} from "lucide-react";

const CAT_ICON = {
  communication: Phone,
  it: Laptop,
  access: KeySquare,
  optional: Boxes,
  custom: PackageOpen,
};
const CAT_LABEL = {
  communication: "Communication",
  it: "IT Equipment",
  access: "Access & Accounts",
  optional: "Optional Assets",
  custom: "Other / Custom",
};
const STATUS_BADGE = {
  draft: { label: "Draft", cls: "bg-slate-200 text-slate-700" },
  pending_employee_ack: {
    label: "Pending Employee Ack",
    cls: "bg-amber-100 text-amber-800",
  },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800" },
  return_in_progress: {
    label: "Return in Progress",
    cls: "bg-sky-100 text-sky-800",
  },
  closed: { label: "Closed", cls: "bg-slate-300 text-slate-800" },
};
const ITEM_STATUS_TONE = {
  issued: "bg-emerald-100 text-emerald-800",
  not_issued: "bg-slate-100 text-slate-600",
  returned: "bg-sky-100 text-sky-800",
  lost: "bg-red-100 text-red-800",
  damaged: "bg-amber-100 text-amber-800",
};

export default function EmployeeAssetHandover() {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [template, setTemplate] = useState({ items: [] });
  const [filterStatus, setFilterStatus] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [viewRec, setViewRec] = useState(null);
  const [busy, setBusy] = useState(false);

  const blank = {
    employee_id: "",
    handover_date: new Date().toISOString().slice(0, 10),
    issued_by_role: "hr",
    approver_role: "super_admin",
    approver_id: "",
    items: [],
    custom_items: [],
    remarks: "",
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    api
      .get("/employee-assets/template")
      .then(({ data }) => setTemplate(data))
      .catch(() => toast.error("Could not load template"));
    api
      .get("/employees?limit=500")
      .then(({ data }) =>
        setEmployees(Array.isArray(data) ? data : data.items || []),
      )
      .catch(() => {});
  }, []);

  const refresh = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/employee-assets/dashboard"),
        api.get(
          `/employee-assets/${
            filterStatus !== "all" ? `?status=${filterStatus}` : ""
          }`,
        ),
      ]);
      setDash(d.data);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    refresh();
  }, [filterStatus]);

  const openCreate = () => {
    let tpl = template?.items || [];
    if (tpl.length === 0) {
      toast.error("Template not ready — please retry in a moment");
      return;
    }
    setForm({
      ...blank,
      items: tpl.map((t) => ({
        key: t.key,
        label: t.label,
        category: t.category,
        value_required: t.value_required,
        value_label: t.value_label,
        value: "",
        status: "not_issued",
        notes: "",
      })),
      custom_items: [],
    });
    setDlg(true);
  };

  const onItemChange = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const addCustomItem = () => {
    setForm((f) => ({
      ...f,
      custom_items: [
        ...f.custom_items,
        {
          key: `custom_${Date.now()}`,
          label: "",
          category: "custom",
          value: "",
          status: "not_issued",
          notes: "",
        },
      ],
    }));
  };
  const updateCustom = (idx, patch) => {
    setForm((f) => ({
      ...f,
      custom_items: f.custom_items.map((it, i) =>
        i === idx ? { ...it, ...patch } : it,
      ),
    }));
  };
  const removeCustom = (idx) => {
    setForm((f) => ({
      ...f,
      custom_items: f.custom_items.filter((_, i) => i !== idx),
    }));
  };

  const submit = async () => {
    if (!form.employee_id) {
      toast.error("Select employee");
      return;
    }
    if (form.approver_role === "reporting_manager" && !form.approver_id) {
      toast.error("Pick the Reporting Manager");
      return;
    }
    setBusy(true);
    try {
      // Strip extra fields not in backend schema
      const items = form.items.map((it) => ({
        key: it.key,
        value: it.value || null,
        status: it.status,
        notes: it.notes || null,
      }));
      const custom_items = form.custom_items
        .filter((it) => it.label?.trim())
        .map((it) => ({
          key: it.key,
          label: it.label,
          value: it.value || null,
          status: it.status,
          notes: it.notes || null,
        }));
      const payload = {
        employee_id: form.employee_id,
        handover_date: form.handover_date,
        issued_by_role: form.issued_by_role,
        approver_role: form.approver_role,
        approver_id:
          form.approver_role === "reporting_manager"
            ? form.approver_id
            : null,
        items,
        custom_items,
        remarks: form.remarks || null,
      };
      const { data } = await api.post("/employee-assets/", payload);
      // Auto-issue (HR signs as Issued By) immediately
      try {
        await api.post(`/employee-assets/${data.id}/issue`);
      } catch (e) {
        /* non-fatal */
      }
      toast.success("Handover created — awaiting approval");
      setDlg(false);
      setForm(blank);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (hid) => {
    try {
      await api.post(`/employee-assets/${hid}/approve`);
      toast.success("Approved — awaiting employee acknowledgement");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const startReturn = async (hid) => {
    const reason = window.prompt(
      "Reason for return? (separation / transfer / replacement)",
      "separation",
    );
    if (!reason) return;
    try {
      await api.post(`/employee-assets/${hid}/start-return`, { reason });
      toast.success("Return flow started");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (hid) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/employee-assets/${hid}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const employee = employees.find((e) => e.id === form.employee_id);

  return (
    <div className="space-y-6" data-testid="employee-asset-handover">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 mb-1">
          <Briefcase className="h-3.5 w-3.5" /> Phase 16.5 · HR Onboarding
        </div>
        <h1
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: "Chivo" }}
        >
          Employee Asset Handover
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Issue mobile, laptop, IT peripherals, account access, and other
          assets to a new joiner. Track returns on separation / transfer.
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Records" value={dash?.total ?? 0} tone="slate" />
        <KpiTile
          label="Active Holders"
          value={dash?.active_holders ?? 0}
          tone="emerald"
        />
        <KpiTile
          label="Pending Employee Ack"
          value={dash?.pending_employee_ack ?? 0}
          tone="amber"
        />
        <KpiTile
          label="Closed"
          value={dash?.by_status?.closed ?? 0}
          tone="slate"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-64" data-testid="ea-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_BADGE).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="flex-1" />
        <Button
          onClick={openCreate}
          data-testid="ea-add-btn"
          className="bg-bravura hover:bg-bravura-700"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Handover
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-xs text-slate-400">
              No asset handovers yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Dept · Designation</th>
                    <th className="text-left px-3 py-2">Issued By</th>
                    <th className="text-left px-3 py-2">Approver</th>
                    <th className="text-right px-3 py-2">Items Issued</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = STATUS_BADGE[r.status] || {
                      label: r.status,
                      cls: "bg-slate-100",
                    };
                    return (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{r.handover_date}</td>
                        <td className="px-3 py-2">
                          <div className="font-semibold">
                            {r.employee_name || "—"}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {r.employee_code}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[11px]">
                          {r.department || "—"}
                          {r.designation ? ` · ${r.designation}` : ""}
                        </td>
                        <td className="px-3 py-2 text-[11px]">
                          {r.issued_by_name ? (
                            <span className="text-emerald-700">
                              ✓ {r.issued_by_name}
                            </span>
                          ) : (
                            <span className="text-amber-700">Pending</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px]">
                          {r.approver_name ? (
                            <span className="text-emerald-700">
                              ✓ {r.approver_name}
                            </span>
                          ) : (
                            <span className="text-amber-700">Pending</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {r.issued_count || 0}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}
                          >
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => setViewRec(r)}
                            className="text-bravura-700 text-[10px] hover:underline mr-2"
                            data-testid={`ea-view-${r.id}`}
                          >
                            View
                          </button>
                          {r.status === "draft" &&
                            r.issued_at &&
                            !r.approved_at && (
                              <button
                                onClick={() => approve(r.id)}
                                className="text-emerald-700 text-[10px] hover:underline mr-2"
                                data-testid={`ea-approve-${r.id}`}
                              >
                                Approve
                              </button>
                            )}
                          {r.status === "completed" && (
                            <button
                              onClick={() => startReturn(r.id)}
                              className="text-sky-700 text-[10px] hover:underline mr-2"
                              data-testid={`ea-return-${r.id}`}
                            >
                              Start Return
                            </button>
                          )}
                          {r.status === "draft" && (
                            <button
                              onClick={() => remove(r.id)}
                              className="text-red-600 text-[10px] hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Employee Asset Handover</DialogTitle>
            <DialogDescription>
              {template?.items?.length || 28}-item catalog across
              Communication, IT, Access, Optional + custom rows.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee *">
              <Select
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
              >
                <SelectTrigger data-testid="ea-form-employee">
                  <SelectValue placeholder="Search employee" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} ({e.employee_code || "—"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Handover Date">
              <Input
                type="date"
                value={form.handover_date}
                onChange={(e) =>
                  setForm({ ...form, handover_date: e.target.value })
                }
              />
            </Field>
            <Field label="Issued By">
              <Select
                value={form.issued_by_role}
                onValueChange={(v) => setForm({ ...form, issued_by_role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="it_admin">IT Admin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Approver">
              <Select
                value={form.approver_role}
                onValueChange={(v) => setForm({ ...form, approver_role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="reporting_manager">
                    Reporting Manager
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.approver_role === "reporting_manager" && (
              <div className="col-span-2">
                <Field label="Reporting Manager *">
                  <Select
                    value={form.approver_id}
                    onValueChange={(v) =>
                      setForm({ ...form, approver_id: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select RM" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} ({e.designation || e.employee_code || "—"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}
          </div>

          {/* Employee auto-fill banner */}
          {employee && (
            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Code</div>
                <div className="font-semibold">{employee.employee_code || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Dept</div>
                <div className="font-semibold">{employee.department || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">
                  Designation
                </div>
                <div className="font-semibold">{employee.designation || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Email</div>
                <div className="font-semibold truncate">
                  {employee.email || "—"}
                </div>
              </div>
            </div>
          )}

          {/* Items by category */}
          {["communication", "it", "access", "optional"].map((cat) => {
            const itemsInCat = form.items
              .map((it, idx) => ({ it, idx }))
              .filter(({ it }) => it.category === cat);
            if (itemsInCat.length === 0) return null;
            const Ic = CAT_ICON[cat];
            return (
              <div key={cat} className="border-t border-slate-200 pt-3 mt-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 mb-2">
                  <Ic className="h-3.5 w-3.5" /> {CAT_LABEL[cat]} (
                  {itemsInCat.length})
                </div>
                <div className="space-y-1.5">
                  {itemsInCat.map(({ it, idx }) => (
                    <div
                      key={it.key}
                      className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg p-2"
                      data-testid={`ea-item-${it.key}`}
                    >
                      <div className="col-span-4 text-xs font-medium">
                        {it.label}
                      </div>
                      <div className="col-span-2">
                        <Select
                          value={it.status}
                          onValueChange={(v) =>
                            onItemChange(idx, { status: v })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="issued">Issued</SelectItem>
                            <SelectItem value="not_issued">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        {it.value_required && (
                          <Input
                            placeholder={it.value_label || "Value"}
                            value={it.value || ""}
                            onChange={(e) =>
                              onItemChange(idx, { value: e.target.value })
                            }
                            className="h-8 text-xs"
                          />
                        )}
                      </div>
                      <div className="col-span-3">
                        <Input
                          placeholder="Notes"
                          value={it.notes || ""}
                          onChange={(e) =>
                            onItemChange(idx, { notes: e.target.value })
                          }
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Custom items */}
          <div className="border-t border-slate-200 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                <PackageOpen className="h-3.5 w-3.5" /> Other (custom items)
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addCustomItem}
                data-testid="ea-add-custom"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Row
              </Button>
            </div>
            {form.custom_items.length === 0 && (
              <div className="text-[11px] text-slate-400">
                Add any extra item not in the standard catalogue.
              </div>
            )}
            <div className="space-y-1.5">
              {form.custom_items.map((it, idx) => (
                <div
                  key={it.key}
                  className="grid grid-cols-12 gap-2 items-center bg-amber-50 rounded-lg p-2"
                >
                  <div className="col-span-4">
                    <Input
                      placeholder="Item name *"
                      value={it.label}
                      onChange={(e) =>
                        updateCustom(idx, { label: e.target.value })
                      }
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={it.status}
                      onValueChange={(v) => updateCustom(idx, { status: v })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="issued">Issued</SelectItem>
                        <SelectItem value="not_issued">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      placeholder="Value/serial"
                      value={it.value || ""}
                      onChange={(e) =>
                        updateCustom(idx, { value: e.target.value })
                      }
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      placeholder="Notes"
                      value={it.notes || ""}
                      onChange={(e) =>
                        updateCustom(idx, { notes: e.target.value })
                      }
                      className="h-8 text-xs"
                    />
                  </div>
                  <button
                    onClick={() => removeCustom(idx)}
                    className="col-span-1 text-red-600 text-[10px] hover:underline"
                  >
                    <X className="h-4 w-4 inline" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3 mt-3">
            <Field label="Remarks">
              <Textarea
                rows={2}
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} data-testid="ea-form-save">
              {busy ? "Saving…" : "Save & Issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewRec} onOpenChange={(o) => !o && setViewRec(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Asset Handover · {viewRec?.employee_name}</DialogTitle>
            <DialogDescription>
              {viewRec?.handover_date} · {viewRec?.employee_code} ·{" "}
              {viewRec?.department}
            </DialogDescription>
          </DialogHeader>
          {viewRec && (
            <RecordDetail
              hid={viewRec.id}
              onChange={() => {
                refresh();
              }}
              onClose={() => {
                refresh();
                setViewRec(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecordDetail({ hid, onChange, onClose }) {
  const [doc, setDoc] = useState(null);
  const [returnMode, setReturnMode] = useState(false);
  const [returns, setReturns] = useState({}); // key -> {status, notes}
  const [closeNotes, setCloseNotes] = useState("");
  useEffect(() => {
    api
      .get(`/employee-assets/${hid}`)
      .then(({ data }) => {
        setDoc(data);
        if (data.status === "return_in_progress") {
          setReturnMode(true);
        }
      })
      .catch(() => {});
  }, [hid]);
  if (!doc) return <div className="text-xs text-slate-400">Loading…</div>;

  const allItems = [...(doc.items || []), ...(doc.custom_items || [])];
  const issued = allItems.filter((it) => it.status === "issued");

  const printPdf = () => {
    window.print();
  };

  const closeRecord = async () => {
    if (!window.confirm("Close this record? Items not selected will remain marked 'issued'.")) return;
    try {
      const items = Object.entries(returns).map(([key, v]) => ({
        key,
        status: v.status,
        notes: v.notes || null,
      }));
      await api.post(`/employee-assets/${hid}/close`, {
        items,
        notes: closeNotes,
      });
      toast.success("Record closed");
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Close failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="ea-record-detail">
      {/* Signatures bar */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <SigBlock
          label="Issued By"
          name={doc.issued_by_name}
          at={doc.issued_at}
        />
        <SigBlock
          label={
            doc.approver_role === "reporting_manager"
              ? "Approved (RM)"
              : "Approved"
          }
          name={doc.approver_name}
          at={doc.approved_at}
        />
        <SigBlock
          label="Employee Ack"
          name={doc.employee_signature_name}
          at={doc.employee_acknowledged_at}
          pending="Awaiting"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={printPdf}>
          <Printer className="h-3.5 w-3.5 mr-1" /> Print / PDF
        </Button>
        {doc.status === "return_in_progress" && (
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={closeRecord}
            data-testid="ea-close-record"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Close Record
          </Button>
        )}
      </div>

      {/* Items grouped */}
      {["communication", "it", "access", "optional", "custom"].map((cat) => {
        const list = allItems.filter((it) => it.category === cat);
        if (list.length === 0) return null;
        const Ic = CAT_ICON[cat];
        return (
          <div key={cat} className="border rounded-lg">
            <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-600 flex items-center gap-1.5">
              <Ic className="h-3 w-3" /> {CAT_LABEL[cat]}
            </div>
            <div className="divide-y divide-slate-100">
              {list.map((it) => (
                <div
                  key={it.key}
                  className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-xs"
                >
                  <div className="col-span-4 font-semibold">{it.label}</div>
                  <div className="col-span-3 text-slate-700">
                    {it.value || (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                        ITEM_STATUS_TONE[it.status] || "bg-slate-100"
                      }`}
                    >
                      {it.status}
                    </span>
                  </div>
                  {returnMode && it.status === "issued" ? (
                    <div className="col-span-3">
                      <Select
                        value={returns[it.key]?.status || ""}
                        onValueChange={(v) =>
                          setReturns((r) => ({
                            ...r,
                            [it.key]: {
                              ...(r[it.key] || {}),
                              status: v,
                            },
                          }))
                        }
                      >
                        <SelectTrigger className="h-7 text-[10px]">
                          <SelectValue placeholder="Return status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="returned">Returned</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                          <SelectItem value="damaged">Damaged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="col-span-3 text-[10px] text-slate-500">
                      {it.notes || it.return_notes || ""}
                      {it.return_date && (
                        <div className="text-[9px] text-slate-400">
                          Returned: {it.return_date}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {returnMode && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Closing notes
          </Label>
          <Textarea
            rows={2}
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder="E.g. Cleared all dues, exit interview done."
          />
        </div>
      )}

      {doc.remarks && (
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">
            Remarks
          </div>
          <div className="text-xs">{doc.remarks}</div>
        </div>
      )}

      <div className="text-[11px] text-slate-500">
        {issued.length} item(s) issued · Status:{" "}
        <strong>{doc.status}</strong>
      </div>
    </div>
  );
}

function SigBlock({ label, name, at, pending }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2">
      <div className="text-[10px] uppercase text-slate-500 font-semibold">
        {label}
      </div>
      {name ? (
        <>
          <div className="text-xs font-semibold text-emerald-700">{name}</div>
          <div className="text-[10px] text-slate-500">
            {at ? at.slice(0, 19).replace("T", " ") : ""}
          </div>
        </>
      ) : (
        <div className="text-xs text-amber-700">{pending || "Pending"}</div>
      )}
    </div>
  );
}

function KpiTile({ label, value, tone, sub }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900",
    rose: "bg-red-50 text-red-900",
    amber: "bg-amber-50 text-amber-900",
    emerald: "bg-emerald-50 text-emerald-900",
  };
  return (
    <div className={`rounded-xl p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">
        {label}
      </div>
      <div className="text-xl font-bold mt-0.5" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
